import PropTypes from 'prop-types'
import React, { useRef } from 'react'
import * as ReactDOMServer from 'react-dom/server'
import styled from 'styled-components'
import { A11y, Autoplay, Navigation, Pagination } from 'swiper'
import 'swiper/css/bundle'
import { Swiper, SwiperSlide } from 'swiper/react'
import { CARD_ELEVATION } from 'components/Card'
import {
  CarouselNavigation,
  CarouselPaginationDot,
} from 'components/CarouselNavigation'
import { CarouselNavigationArrow } from 'components/CarouselNavigation/CarouselNavigationArrow'
import { Box, Flex } from 'components/Layout'
import Testimonial from 'components/Testimonial'
import { H4 } from 'components/Typography'
import { RADIUS, SPACE, Z_INDEX } from 'Theme'
import { useMedia } from 'useMedia'
import { formatWithExpertUser } from 'utils/formatters'

const TESTIMONIAL_AUTOPLAY_DELAY = 15000
const BULLET_THRESHOLD = 10

const StyledSwiper = styled(Swiper)`
  width: 100%;
  height: 100%;
  box-shadow: ${CARD_ELEVATION.ELEVATION_3};
  border-radius: ${RADIUS.PX_10};
  position: static;
`

const BULLET_ACTIVE_CLASS =
  'expert-testimonials-widget-pagination-bullet-active'

const ExpertTestimonialsCarousel = ({ testimonials }) => {
  const media = useMedia()
  const paginationRef = useRef(null)
  const nextButtonRef = useRef(null)
  const prevButtonRef = useRef(null)

  const shouldDisplayArrows = !media.MOBILE && !media.TABLET

  return (
    <Box>
      <Flex
        alignItems="center"
        justifyContent={shouldDisplayArrows ? 'space-between' : 'center'}
        mb={SPACE.PX_30}
      >
        <H4 as="h2" mb="unset">
          Client Testimonials
        </H4>
        {shouldDisplayArrows && (
          <Flex gap={SPACE.PX_20}>
            <CarouselNavigationArrow
              isLtr={false}
              hasBackground={false}
              ref={prevButtonRef}
            />
            <CarouselNavigationArrow
              isLtr
              hasBackground={false}
              ref={nextButtonRef}
            />
          </Flex>
        )}
      </Flex>
      <Box position="relative">
        <StyledSwiper
          modules={[Pagination, Autoplay, A11y, Navigation]}
          isMobile={media.MOBILE}
          autoplay={{ delay: TESTIMONIAL_AUTOPLAY_DELAY }}
          slidesPerView="auto"
          observer
          observeParents
          observeSlideChildren
          pagination={{
            el: paginationRef.current,
            clickable: true,
            bulletActiveClass: BULLET_ACTIVE_CLASS,
            renderBullet: (_, className) =>
              ReactDOMServer.renderToStaticMarkup(
                <CarouselPaginationDot
                  className={className}
                  isAutoRotationEnabled={false}
                  animationDuration={4}
                  bulletActiveClass={BULLET_ACTIVE_CLASS}
                  slidesCount={testimonials.length}
                  isDarkTheme
                />
              ),
            dynamicBullets: testimonials.length > BULLET_THRESHOLD,
          }}
          navigation={{
            prevEl: prevButtonRef.current,
            nextEl: nextButtonRef.current,
          }}
          hasDynamicBullets={testimonials.length > BULLET_THRESHOLD}
        >
          {testimonials.map((testimonial) => (
            <SwiperSlide key={testimonial.id}>
              <Testimonial
                clientName={testimonial.clientName}
                feedback={testimonial.feedback}
                consultationName={testimonial.consultationName}
                expertName={formatWithExpertUser(testimonial.expertUser)}
              />
            </SwiperSlide>
          ))}
        </StyledSwiper>
        <Flex
          width="100%"
          justifyContent="center"
          zIndex={Z_INDEX.CAROUSEL_NAVIGATION_WRAPPER}
          mt={SPACE.PX_8}
        >
          <CarouselNavigation
            hasArrows={false}
            paginationRef={paginationRef}
            dotsWrapperStyles={{ px: '0 !important', justifyContent: 'start' }}
          />
        </Flex>
      </Box>
    </Box>
  )
}

ExpertTestimonialsCarousel.propTypes = {
  testimonials: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      clientName: PropTypes.string,
      feedback: PropTypes.string,
      consultationName: PropTypes.string,
      expertUser: PropTypes.shape({
        firstName: PropTypes.string,
        lastName: PropTypes.string,
        title: PropTypes.string,
      }),
    })
  ).isRequired,
}

export default ExpertTestimonialsCarousel
